package com.tovtalk.ui.theme

import androidx.compose.runtime.Immutable
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.graphics.Color

/**
 * TovTalk Custom Color System - Shared across Android, iOS, Web
 * Minimalist, modern, content-first design
 */

// ==================== Brand Colors (Blue is the Action) ====================
val TovTalkBrand = Color(0xFF2563EB)       // Blue 600 - CTA, Like Active
val TovTalkBrandLight = Color(0xFF3B82F6)  // Blue 500 - Hover
val TovTalkBrandDark = Color(0xFF1D4ED8)   // Blue 700 - Pressed
val TovTalkBrandSubtle = Color(0xFFDBEAFE) // Blue 100 - Subtle Container

// ==================== Semantic Colors ====================
val SuccessGreen = Color(0xFF10B981)
val WarningOrange = Color(0xFFF59E0B)
val ErrorRed = Color(0xFFEF4444)
val LikeActive = Color(0xFF2563EB)  // Blue 600 - Like when active (same as brand)
val GiftPink = Color(0xFFE91E63)    // Gift notification badge
val WhatsAppGreen = Color(0xFF25D366) // WhatsApp share button
val WhatsAppBusinessTeal = Color(0xFF128C7E) // WhatsApp Business share button
val ChatTickBlue = Color(0xFF007AFF) // Read receipt tick
val ChatTickGray = Color(0xFF8E8E93) // Sent receipt tick

// ==================== Neutral Colors (Dark) ====================
val DarkBackground = Color(0xFF000000)
val DarkSurface = Color(0xFF0A0A0A)
val DarkSurfaceElevated = Color(0xFF141414)
val DarkSurfaceOverlay = Color(0xFF1C1C1E)
val DarkBorder = Color(0xFF2C2C2E)
val DarkTextPrimary = Color(0xFFFFFFFF)
val DarkTextSecondary = Color(0xFFAAAAAA)
val DarkTextTertiary = Color(0xFF666666)
val DarkTextDisabled = Color(0xFF444444)

// ==================== Neutral Colors (Light) ====================
val LightBackground = Color(0xFFFFFFFF)
val LightSurface = Color(0xFFFAFAFA)
val LightSurfaceElevated = Color(0xFFFFFFFF)
val LightSurfaceOverlay = Color(0xFFF2F2F7)
val LightBorder = Color(0xFFE5E5EA)
val LightTextPrimary = Color(0xFF000000)
val LightTextSecondary = Color(0xFF666666)
val LightTextTertiary = Color(0xFF999999)
val LightTextDisabled = Color(0xFFCCCCCC)

// ==================== Overlay Colors ====================
val OverlayLight = Color(0x33FFFFFF)
val OverlayMedium = Color(0x66FFFFFF)
val OverlayDark = Color(0x99000000)
val OverlayScrim = Color(0xB3000000)

@Immutable
data class TovTalkExtendedColors(
    val brand: Color,
    val brandLight: Color,
    val gradientStart: Color,
    val gradientEnd: Color,
    val success: Color,
    val warning: Color,
    val error: Color,
    val like: Color,
    val comment: Color,
    val share: Color,
    val follow: Color,
    val backgroundPrimary: Color,
    val backgroundSecondary: Color,
    val surfaceElevated: Color,
    val surfaceOverlay: Color,
    val border: Color,
    val textPrimary: Color,
    val textSecondary: Color,
    val textTertiary: Color,
    val textDisabled: Color,
    val textOnBrand: Color,
    val onBrand: Color,
    val overlayLight: Color,
    val overlayMedium: Color,
    val overlayDark: Color,
    val overlayScrim: Color,
    val videoOverlayGradientTop: Color,
    val videoOverlayGradientBottom: Color,
    val videoBackground: Color,
    val drawerScrim: Color,
    val drawerSurface: Color,
    val chatBubbleTheirs: Color,
    val chatTickRead: Color,
    val chatTickSent: Color,
    val gift: Color,
    val whatsApp: Color,
    val whatsAppBusiness: Color,
)

val DarkExtendedColors = TovTalkExtendedColors(
    brand = TovTalkBrand,
    brandLight = TovTalkBrandLight,
    gradientStart = TovTalkBrand,
    gradientEnd = TovTalkBrandLight,
    success = SuccessGreen,
    warning = WarningOrange,
    error = ErrorRed,
    like = LikeActive,
    comment = TovTalkBrand,
    share = TovTalkBrand,
    follow = TovTalkBrand,
    backgroundPrimary = DarkBackground,
    backgroundSecondary = DarkSurface,
    surfaceElevated = DarkSurfaceElevated,
    surfaceOverlay = DarkSurfaceOverlay,
    border = DarkBorder,
    textPrimary = DarkTextPrimary,
    textSecondary = DarkTextSecondary,
    textTertiary = DarkTextTertiary,
    textDisabled = DarkTextDisabled,
    textOnBrand = Color.White,
    onBrand = Color.White,
    overlayLight = OverlayLight,
    overlayMedium = OverlayMedium,
    overlayDark = OverlayDark,
    overlayScrim = OverlayScrim,
    videoOverlayGradientTop = Color(0x4D000000),
    videoOverlayGradientBottom = Color(0x99000000),
    videoBackground = DarkBackground,
    drawerScrim = Color.Black.copy(alpha = 0.45f),
    drawerSurface = Color(0xFF121212),
    chatBubbleTheirs = Color(0xFF2C2C2E),
    chatTickRead = ChatTickBlue,
    chatTickSent = ChatTickGray,
    gift = GiftPink,
    whatsApp = WhatsAppGreen,
    whatsAppBusiness = WhatsAppBusinessTeal,
)

val LightExtendedColors = TovTalkExtendedColors(
    brand = TovTalkBrand,
    brandLight = TovTalkBrandLight,
    gradientStart = TovTalkBrand,
    gradientEnd = TovTalkBrandLight,
    success = SuccessGreen,
    warning = WarningOrange,
    error = ErrorRed,
    like = LikeActive,
    comment = TovTalkBrand,
    share = TovTalkBrand,
    follow = TovTalkBrand,
    backgroundPrimary = LightBackground,
    backgroundSecondary = LightSurface,
    surfaceElevated = LightSurfaceElevated,
    surfaceOverlay = LightSurfaceOverlay,
    border = LightBorder,
    textPrimary = LightTextPrimary,
    textSecondary = LightTextSecondary,
    textTertiary = LightTextTertiary,
    textDisabled = LightTextDisabled,
    textOnBrand = Color.White,
    onBrand = Color.White,
    overlayLight = OverlayLight,
    overlayMedium = OverlayMedium,
    overlayDark = Color(0x66000000),
    overlayScrim = Color(0x99000000),
    videoOverlayGradientTop = Color(0x33000000),
    videoOverlayGradientBottom = Color(0x66000000),
    videoBackground = DarkBackground,
    drawerScrim = Color.Black.copy(alpha = 0.45f),
    drawerSurface = Color(0xFF121212),
    chatBubbleTheirs = Color(0xFFE8E8ED),
    chatTickRead = ChatTickBlue,
    chatTickSent = ChatTickGray,
    gift = GiftPink,
    whatsApp = WhatsAppGreen,
    whatsAppBusiness = WhatsAppBusinessTeal,
)

val LocalTovTalkColors = staticCompositionLocalOf { DarkExtendedColors }
